import type { RouterScrollBehavior } from 'vue-router'

// Scroll to top on every new navigation; back/forward keeps the saved
// position so the browser's chrome buttons feel native. Without this,
// Vue Router preserves scroll across pushState navigations — clicking
// /privacy from the bottom of one page lands you at the bottom of the
// privacy policy.
//
// Hash navigations (/#pricing — the /pricing redirect target) resolve to
// the anchor element instead. Two timing traps make this more than a
// one-shot `{ el: to.hash }` (release-test-plan row P2, QA repro
// 2026-07-31):
//
//  1. main.ts mounts the app without awaiting router.isReady(), so on a
//     cold load the router settles the initial navigation before the
//     view's DOM exists — the target is polled for, not assumed.
//  2. The page keeps settling after the router applies the scroll: web
//     fonts swap in and async data-driven content changes the height of
//     everything above the anchor, stranding the viewport hundreds —
//     measured up to ~1900 — px away from the section it just scrolled
//     to, and on a slow cold load that content arrives several seconds
//     in. The anchor is therefore re-checked every 250ms for 8s and the
//     viewport re-aligned on drift. The pass stops the moment the
//     visitor scrolls themselves (wheel, pointerdown, keydown —
//     pointerdown also covers scrollbar drags and touch), and a new
//     navigation cancels the previous pass so it can never fight the
//     next scroll. The anchor is re-resolved by id on every tick so a
//     re-render that replaces the element doesn't kill the pass.
//
// If the target never mounts (stale anchor in a shared link), fall back
// to top rather than leaving the viewport wherever the browser put it.
const POLL_INTERVAL_MS = 100
const POLL_ATTEMPTS = 50

const SETTLE_POLL_MS = 250
const SETTLE_MAX_MS = 8000
const SETTLE_TOLERANCE_PX = 4
const USER_TAKEOVER_EVENTS = ['wheel', 'pointerdown', 'keydown'] as const

function waitForHashTarget(id: string): Promise<Element | null> {
  return new Promise((resolve) => {
    function attempt(remaining: number) {
      const el = document.getElementById(id)
      if (el) return resolve(el)
      if (remaining <= 0) return resolve(null)
      window.setTimeout(() => attempt(remaining - 1), POLL_INTERVAL_MS)
    }
    attempt(POLL_ATTEMPTS)
  })
}

let cancelActiveSettle: (() => void) | null = null

function realignWhileSettling(id: string) {
  let elapsed = 0
  const stop = () => {
    cancelActiveSettle = null
    window.clearInterval(interval)
    for (const ev of USER_TAKEOVER_EVENTS) window.removeEventListener(ev, stop)
  }
  const interval = window.setInterval(() => {
    elapsed += SETTLE_POLL_MS
    if (elapsed >= SETTLE_MAX_MS) return stop()
    const el = document.getElementById(id)
    if (!el) return // mid-re-render; the anchor may be back next tick
    const drift = el.getBoundingClientRect().top
    if (Math.abs(drift) > SETTLE_TOLERANCE_PX) window.scrollBy(0, drift)
  }, SETTLE_POLL_MS)
  for (const ev of USER_TAKEOVER_EVENTS) window.addEventListener(ev, stop, { passive: true })
  cancelActiveSettle = stop
}

export const hashScrollBehavior: RouterScrollBehavior = async (to, _from, savedPosition) => {
  cancelActiveSettle?.()
  if (savedPosition) return savedPosition
  if (to.hash) {
    const id = decodeURIComponent(to.hash.slice(1))
    const el = await waitForHashTarget(id)
    if (el) {
      realignWhileSettling(id)
      return { el }
    }
  }
  return { top: 0 }
}
